import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

/**
 * Hook for copying text to clipboard with notifications
 * @param {Object} options - Hook parameters
 * @param {Function} options.onSuccess - Function called on successful copying
 * @param {Function} options.onError - Function called on copy error
 * @param {Function} options.getSuccessMessage - Function to get the text of a successful notification text
 * @param {Function} options.getErrorMessage - Function to get the text of an error notification.
 * @returns {Object} - Object with copy function and visibility indicator
 */
export const useClipboard = ({
  onSuccess,
  onError,
  getSuccessMessage,
  getErrorMessage,
}: {
  onSuccess?: () => void;
  onError?: (err: unknown) => void;
  getSuccessMessage?: () => string;
  getErrorMessage?: () => string;
}) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);

  const successMessage = getSuccessMessage ? getSuccessMessage() : t('toaster.copySave');
  const errorMessage = getErrorMessage ? getErrorMessage() : t('error.copy');

  const copyToClipboard = async (textToCopy: string) => {
    try {
      await navigator.clipboard.writeText(textToCopy);
      toast.success(successMessage);
      setIsVisible(true);
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      toast.error(errorMessage);
      console.error('Copy error:', err);
      if (onError) {
        onError(err);
      }
    }
  };

  return {
    copyToClipboard,
    isVisible,
    setIsVisible,
  };
};
